import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { FaComments, FaTimes, FaPaperPlane, FaThumbsUp, FaThumbsDown, FaHeart, FaRocket, FaHandPaper, FaTrash, FaMapMarkerAlt, FaLaughSquint, FaExclamation, FaBullhorn, FaExclamationTriangle, FaChevronDown, FaDesktop, FaSpinner } from 'react-icons/fa';
import { ChatRoom } from 'amazon-ivs-chat-messaging';
import API from '../../API';
import logger from '../../utils/logger';
import intersectStaffIcon from '../images/intersect-staff-blue-icon.png';
import { useOverlay } from '../../contexts/FocusContext';

type ChatMessageType = 'default' | 'announcement' | 'system';

interface ChatMessage {
  id: string;
  userId: string;
  username: string;
  message: string;
  timestamp: Date;
  isLocal: boolean;
  isIntersectStaff?: boolean;
  chatType?: ChatMessageType;
}

type ReactionType = 'thumbs_up' | 'thumbs_down' | 'heart' | 'celebrate' | 'wave' | 'laugh' | 'exclamation';

interface MessageReaction {
  type: ReactionType;
  userId: string;
  username: string;
}

interface MessageReactions {
  [messageId: string]: {
    thumbs_up: MessageReaction[];
    thumbs_down: MessageReaction[];
    heart: MessageReaction[];
    celebrate: MessageReaction[];
    wave: MessageReaction[];
    laugh: MessageReaction[];
    exclamation: MessageReaction[];
  };
}

interface IVSRealtimeChatProps {
  roomArn?: string;
  messagingEndpoint?: string;
  accessToken?: string;
  userId?: string;
  roomName?: string;
  roomId?: string;
  eventId?: string;
  podiumPosition?: string;
  debugIsIntersectStaff?: boolean; // For sandbox testing - overrides API response
  onPodiumTeleport?: () => void; // Callback when user teleports to podium
  onScreenSharePermissionGranted?: () => void; // Callback when current user is granted screen share permission
  onScreenSharePermissionRevoked?: () => void; // Callback when current user's screen share permission is revoked
  // Tour/onboarding identifier
  dataTourId?: string;
}

export interface IVSRealtimeChatRef {
  focusInput: () => void;
  isIntersectStaff: () => boolean;
  scrollToBottom: () => void;
}

const IVSRealtimeChat = forwardRef<IVSRealtimeChatRef, IVSRealtimeChatProps>(({ 
  roomArn,
  messagingEndpoint,
  accessToken,
  userId,
  roomName,
  roomId,
  eventId,
  podiumPosition,
  debugIsIntersectStaff,
  onPodiumTeleport,
  onScreenSharePermissionGranted,
  onScreenSharePermissionRevoked,
  dataTourId
}, ref) => {
  const [isOpen, setIsOpen] = useState(() => {
    // Persist chat open/closed state across room switches
    const saved = localStorage.getItem('ivs-chat-open');
    return saved !== null ? saved === 'true' : true; // Default to open if no saved state
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const isOpenRef = useRef(isOpen); // Ref to track isOpen in callbacks
  const countedMessageIds = useRef<Set<string>>(new Set()); // Track which messages we've already counted
  
  // Keep ref in sync with state
  useEffect(() => {
    isOpenRef.current = isOpen;
    // Clear unread count when chat is opened
    if (isOpen) {
      setUnreadCount(0);
      countedMessageIds.current.clear(); // Clear tracked IDs when opening chat
    }
  }, [isOpen]);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [localUsername, setLocalUsername] = useState<string>(userId || 'Unknown');
  const [localIsIntersectStaffFromAPI, setLocalIsIntersectStaffFromAPI] = useState(false);
  const [reactions, setReactions] = useState<MessageReactions>({});
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [deleteConfirmMessageId, setDeleteConfirmMessageId] = useState<string | null>(null);
  const [isDeletingMessage, setIsDeletingMessage] = useState(false);
  const [teleportConfirmMessage, setTeleportConfirmMessage] = useState<ChatMessage | null>(null);
  const [screenShareConfirmMessage, setScreenShareConfirmMessage] = useState<ChatMessage | null>(null);
  const [screenShareRevokeConfirmMessage, setScreenShareRevokeConfirmMessage] = useState<ChatMessage | null>(null);
  const [selectedChatType, setSelectedChatType] = useState<ChatMessageType>('default');
  const [showChatTypeDropdown, setShowChatTypeDropdown] = useState(false);
  const [messageTimestamps, setMessageTimestamps] = useState<Record<string, number>>({});

  // Use debug override if provided (sandbox), otherwise use API response
  const localIsIntersectStaff = debugIsIntersectStaff !== undefined ? debugIsIntersectStaff : localIsIntersectStaffFromAPI;

  // Debug logging for podium position
  useEffect(() => {
    logger.log('[IVS Chat] Podium position updated:', {
      podiumPosition,
      hasPodium: !!podiumPosition,
      isStaff: localIsIntersectStaff,
      isDebugOverride: debugIsIntersectStaff !== undefined
    });
  }, [podiumPosition, localIsIntersectStaff, debugIsIntersectStaff]);
  
  const chatRoomRef = useRef<ChatRoom | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isInitialLoadRef = useRef(true); // Track if this is initial message load
  
  // Typing state for Unity events (debounced)
  const isTypingRef = useRef(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const TYPING_DEBOUNCE_MS = 1500; // Send "stopped typing" after 1.5s of no input

  // Register as overlay when chat input is focused or confirmation dialogs are open
  // This releases Unity capture so user can type in the chat
  useOverlay('chat-input', isInputFocused || deleteConfirmMessageId !== null || teleportConfirmMessage !== null || screenShareConfirmMessage !== null || screenShareRevokeConfirmMessage !== null);

  // Function to only release cursor (keep keyboard for movement when hovering chat)
  const releaseCursorOnly = () => {
    if (window.sendMessageToUnity) {
      try {
        // Disable cursor capture only - keyboard stays active
        const captureCursorData = {
          eventType: 'CaptureCursor',
          payload: JSON.stringify({ capture: false })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureCursorData));
        logger.log('[Chat→Unity] Released cursor only (keyboard still active)');
      } catch (error) {
        console.error('[Chat→Unity] Error releasing cursor:', error);
      }
    }
  };

  // Send typing status to Unity (debounced to avoid spam)
  const sendTypingStatusToUnity = (isTyping: boolean) => {
    if (window.sendMessageToUnity) {
      try {
        const typingData = {
          eventType: 'IsTyping',
          payload: JSON.stringify({ isTyping })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(typingData));
        logger.log('[Chat→Unity] Sent typing status:', isTyping);
      } catch (error) {
        console.error('[Chat→Unity] Error sending typing status:', error);
      }
    }
  };

  // Handle input change with debounced typing events
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputMessage(value);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (value.length > 0) {
      // Only send "started typing" if we weren't already typing
      if (!isTypingRef.current) {
        isTypingRef.current = true;
        sendTypingStatusToUnity(true);
      }

      // Set timeout to send "stopped typing" after debounce period
      typingTimeoutRef.current = setTimeout(() => {
        isTypingRef.current = false;
        sendTypingStatusToUnity(false);
      }, TYPING_DEBOUNCE_MS);
    } else {
      // Input is empty, send "stopped typing" immediately
      if (isTypingRef.current) {
        isTypingRef.current = false;
        sendTypingStatusToUnity(false);
      }
    }
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    focusInput: () => {
      if (inputRef.current) {
        inputRef.current.focus();
        setIsInputFocused(true);
        // Also open chat if it's closed
        setIsOpen(true);
      }
    },
    isIntersectStaff: () => localIsIntersectStaff,
    scrollToBottom: () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }));

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      if (isInitialLoadRef.current) {
        // For initial load (historical messages), use immediate scroll with a small delay
        // to ensure the DOM has fully rendered all messages
        const scrollTimeout = setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
          }
          isInitialLoadRef.current = false;
        }, 100);
        return () => clearTimeout(scrollTimeout);
      } else {
        // For new messages, use smooth scrolling
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages]);

  // Persist chat open/closed state to localStorage
  useEffect(() => {
    localStorage.setItem('ivs-chat-open', String(isOpen));
  }, [isOpen]);

  // Close chat type dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showChatTypeDropdown) {
        setShowChatTypeDropdown(false);
      }
    };
    
    if (showChatTypeDropdown) {
      // Add a small delay to prevent immediate closing when opening
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showChatTypeDropdown]);

  // Clear state when room changes
  useEffect(() => {
    // Clear messages and reactions when switching rooms
    setMessages([]);
    setReactions({});
    setIsConnected(false);
    // Reset initial load flag so next batch of messages scrolls to bottom
    isInitialLoadRef.current = true;
  }, [roomArn]);

  // Initialize chat room connection
  useEffect(() => {
    if (!roomArn || !messagingEndpoint || !accessToken) {
      return;
    }

    const unsubscribeFunctions: (() => void)[] = [];

    const connectToChatRoom = async () => {
      try {
        // Fetch chat token from API
        const fetchChatToken = async (): Promise<{ token: string; intersectStaff?: boolean }> => {
          try {
            // Use room-specific token if roomId is provided, otherwise fall back to event-level token
            const response = roomId && eventId
              ? await API.getIVSChatTokenForRoom(accessToken, {
                  eventId: eventId,
                  roomId: roomId
                })
              : await API.getIVSChatToken(accessToken, {
                  eventId: eventId || '22222'
            });

            if (response.status !== 200) {
              throw new Error(`Failed to fetch chat token: ${response.status}`);
            }

            const data = await response.json();
            
            // Handle case where data might be the token string directly
            let token: string;
            let intersectStaffValue: boolean | string | undefined;
            
            if (typeof data === 'string') {
              // If data is just the token string
              token = data;
              intersectStaffValue = undefined;
            } else {
              // If data is an object
              token = data.token || data.chatToken || (typeof data === 'string' ? data : '');
              intersectStaffValue = data.intersectStaff;
            }
            
            // Extract and set intersectStaff from response
            if (intersectStaffValue !== undefined) {
              let isIntersectStaff: boolean;
              if (typeof intersectStaffValue === 'boolean') {
                isIntersectStaff = intersectStaffValue;
              } else if (typeof intersectStaffValue === 'string') {
                isIntersectStaff = intersectStaffValue.toLowerCase() === 'true';
              } else {
                isIntersectStaff = Boolean(intersectStaffValue);
              }
              setLocalIsIntersectStaffFromAPI(isIntersectStaff);
            }
            
            // Convert intersectStaff to boolean for return type
            const intersectStaffBool = intersectStaffValue !== undefined 
              ? (typeof intersectStaffValue === 'boolean' 
                  ? intersectStaffValue 
                  : (typeof intersectStaffValue === 'string' 
                      ? intersectStaffValue.toLowerCase() === 'true' 
                      : Boolean(intersectStaffValue)))
              : undefined;
            
            return { token, intersectStaff: intersectStaffBool };
          } catch (error) {
            console.error('Error fetching chat token:', error);
            throw error;
          }
        };

        // Create token provider function
        const tokenProvider = async () => {
          const result = await fetchChatToken();
          return { token: result.token };
        };

        // Create ChatRoom instance
        const chatRoom = new ChatRoom({
          regionOrUrl: messagingEndpoint,
          tokenProvider: tokenProvider,
        });

        chatRoomRef.current = chatRoom;

        // Function to fetch chat history from backend
        const fetchChatHistory = async () => {
          if (!eventId || !roomId || !accessToken) return;
          
          try {
            const response = await API.getChatHistory(accessToken, { eventId, roomId, limit: 200 });
            if (response.status === 200) {
              const data = await response.json();
              
              // Convert API response to ChatMessage format
              const historicalMessages: ChatMessage[] = data.messages.map((msg: any) => ({
                id: msg.id,
                userId: msg.userId,
                username: msg.username,
                message: msg.message,
                timestamp: new Date(msg.createdAt),
                isLocal: msg.userId === userId,
                isIntersectStaff: msg.isIntersectStaff,
                chatType: (msg.chatType || 'default') as ChatMessageType
              }));
              
              // Store timestamps for reactions API
              const timestamps: Record<string, number> = {};
              data.messages.forEach((msg: any) => {
                timestamps[msg.id] = msg.timestamp;
              });
              setMessageTimestamps(timestamps);
              
              // Convert API reactions format to local format
              const historicalReactions: MessageReactions = {};
              data.messages.forEach((msg: any) => {
                if (msg.reactions) {
                  historicalReactions[msg.id] = {
                    thumbs_up: msg.reactions.thumbs_up || [],
                    thumbs_down: msg.reactions.thumbs_down || [],
                    heart: msg.reactions.heart || [],
                    celebrate: msg.reactions.celebrate || [],
                    wave: msg.reactions.wave || [],
                    laugh: msg.reactions.laugh || [],
                    exclamation: msg.reactions.exclamation || []
                  };
                }
              });
              
              setMessages(historicalMessages);
              setReactions(historicalReactions);
              logger.log('[IVS Chat] Loaded', historicalMessages.length, 'historical messages');
            }
          } catch (error) {
            console.error('[IVS Chat] Error fetching chat history:', error);
          }
        };

        // Set up event listeners
        unsubscribeFunctions.push(
          chatRoom.addListener('connect', () => {
            logger.log('Chat room connected');
            setIsConnected(true);
            
            // Fetch historical messages when connected
            fetchChatHistory();
          })
        );

        unsubscribeFunctions.push(
          chatRoom.addListener('disconnect', () => {
            logger.log('Chat room disconnected');
            setIsConnected(false);
          })
        );

        // Listen for message deletions from AWS IVS Chat
        unsubscribeFunctions.push(
          chatRoom.addListener('messageDelete', (deleteEvent: any) => {
            // The messageId field contains the actual message ID to delete
            // The id field is the delete operation ID (not what we need)
            const deletedMessageId = deleteEvent.messageId || 
                                    deleteEvent.MessageId ||
                                    deleteEvent.attributes?.MessageId ||
                                    deleteEvent.attributes?.messageId ||
                                    deleteEvent.id || 
                                    deleteEvent.Id;
            logger.log('[IVS Chat] Received messageDelete event for:', deletedMessageId, 'Full event:', deleteEvent);
            
            if (deletedMessageId) {
              setMessages(prev => {
                logger.log('[IVS Chat] Current message IDs in state:', prev.map(m => ({ id: m.id, content: m.message.substring(0, 20) })));
                const messageExists = prev.some(msg => msg.id === deletedMessageId);
                logger.log('[IVS Chat] Message exists in local state?', messageExists);
                return prev.filter(msg => msg.id !== deletedMessageId);
              });
            }
          })
        );

        // Listen for messages - try multiple event names
        const handleMessage = (message: any) => {
          logger.log('Received chat message:', message);
          
          // Handle IVS Chat message structure: can be Sender (uppercase) or sender (lowercase)
          const messageUserId = message.Sender?.UserId || 
                               message.Sender?.Attributes?.username || 
                               message.Sender?.Attributes?.userId ||
                               message.sender?.userId ||
                               message.sender?.attributes?.userId ||
                               message.attributes?.userId || 
                               message.senderId || 
                               message.userId || 
                               message.sender?.id || 
                               'Unknown';
          
          const messageContent = message.Content || 
                                message.content || 
                                message.text || 
                                message.message || 
                                message.body || 
                                message.payload?.content || '';
          
          // Check if this message is from the current user
          // Extract username to compare
          const messageUsername = message.Sender?.Attributes?.username || 
                                 message.sender?.attributes?.username ||
                                 message.Sender?.Attributes?.userId ||
                                 message.sender?.attributes?.userId ||
                                 message.sender?.userId ||
                                 messageUserId;
          
          const isFromCurrentUser = messageUserId === userId || 
                                   message.Sender?.UserId === userId ||
                                   message.Sender?.Attributes?.userId === userId ||
                                   message.sender?.userId === userId ||
                                   message.sender?.attributes?.userId === userId ||
                                   message.senderId === userId ||
                                   messageUsername === userId ||
                                   (localUsername !== 'Unknown' && messageUsername === localUsername);

          // Check if sender is Intersect staff - try both uppercase and lowercase paths
          const intersectStaffStr = message.Sender?.Attributes?.intersectStaff || 
                                   message.sender?.attributes?.intersectStaff ||
                                   message.attributes?.intersectStaff || 
                                   'false';
          const isIntersectStaff = intersectStaffStr === 'true' || intersectStaffStr === true;

          // Extract chat type from message attributes (for announcements/warnings)
          const chatTypeFromMsg = message.Attributes?.chatType || 
                                 message.attributes?.chatType ||
                                 message.Sender?.Attributes?.chatType ||
                                 'default';

          if (!messageContent) {
            logger.warn('Message has no content, full message:', JSON.stringify(message, null, 2));
            return;
          }

          // Check message type for special messages (reactions, mod commands, etc)
          const messageType = message.Attributes?.messageType || 
                             message.attributes?.messageType;
          
          // Handle moderator teleport command
          if (messageType === 'MOD_TELEPORT') {
            try {
              const targetUserId = message.Attributes?.targetUserId || 
                                  message.attributes?.targetUserId;
              
              // Check if this command is for the current user
              const isForMe = targetUserId === userId || targetUserId === localUsername;
              
              if (isForMe) {
                logger.log('[IVS Chat] 📍 Received moderator teleport command for me!');
                
                // Parse the teleport data from message content
                const teleportData = JSON.parse(messageContent);
                
                if (teleportData.position && window.sendMessageToUnity) {
                  logger.log('[IVS Chat→Unity] Executing local teleport to podium:', teleportData.position);
                  
                  // Trigger local Teleport event using existing Unity bridge
                  const teleportPayload = {
                    eventType: 'Teleport',
                    payload: JSON.stringify({
                      position: teleportData.position,
                      rotation: teleportData.rotation || { x: 0, y: 0, z: 0 }
                    })
                  };
                  
                  window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportPayload));
                  logger.log('[IVS Chat→Unity] Local teleport command sent to Unity');
                  
                  // Notify parent that user is now at podium
                  if (onPodiumTeleport) {
                    onPodiumTeleport();
                  }
                }
              } else {
                logger.log('[IVS Chat] Received moderator teleport command for another user:', targetUserId);
              }
            } catch (error) {
              console.error('[IVS Chat] Error handling moderator teleport:', error);
            }
            // Don't add this as a visible chat message
            return;
          }
          
          // Handle moderator grant screen share command
          if (messageType === 'MOD_GRANT_SCREENSHARE') {
            try {
              const targetUserId = message.Attributes?.targetUserId || 
                                  message.attributes?.targetUserId;
              
              // Check if this command is for the current user
              const isForMe = targetUserId === userId || targetUserId === localUsername;
              
              if (isForMe) {
                logger.log('[IVS Chat] 🎥 Received screen share permission grant!');
                
                // Notify parent that user now has screen share permission
                if (onScreenSharePermissionGranted) {
                  onScreenSharePermissionGranted();
                }
              } else {
                logger.log('[IVS Chat] Received screen share grant for another user:', targetUserId);
              }
            } catch (error) {
              console.error('[IVS Chat] Error handling screen share grant:', error);
            }
            // Don't add this as a visible chat message
            return;
          }
          
          // Handle moderator revoke screen share command
          if (messageType === 'MOD_REVOKE_SCREENSHARE') {
            try {
              const targetUserId = message.Attributes?.targetUserId || 
                                  message.attributes?.targetUserId;
              
              // Check if this command is for the current user
              const isForMe = targetUserId === userId || targetUserId === localUsername;
              
              if (isForMe) {
                logger.log('[IVS Chat] 🚫 Received screen share permission revoke!');
                
                // Notify parent that user's screen share permission is revoked
                if (onScreenSharePermissionRevoked) {
                  onScreenSharePermissionRevoked();
                }
              } else {
                logger.log('[IVS Chat] Received screen share revoke for another user:', targetUserId);
              }
            } catch (error) {
              console.error('[IVS Chat] Error handling screen share revoke:', error);
            }
            // Don't add this as a visible chat message
            return;
          }
          
          // Handle reaction messages
          if (messageType === 'REACTION') {
            try {
              const targetMessageId = message.Attributes?.targetMessageId || 
                                     message.attributes?.targetMessageId;
              const reactionType = (message.Attributes?.reactionType || 
                                   message.attributes?.reactionType) as ReactionType;
              const reactionUserId = message.Attributes?.userId || 
                                    message.attributes?.userId;
              const reactionUsername = message.Attributes?.username || 
                                      message.attributes?.username;
              
              // Skip if this is from the current user (we already have optimistic update)
              const isFromCurrentUser = reactionUserId === userId || 
                                       reactionUserId === localUsername;
              
              if (!isFromCurrentUser && targetMessageId && reactionType) {
                logger.log('[IVS Chat] Received reaction from another user:', { targetMessageId, reactionType, reactionUserId });
                
                setReactions(prev => {
                  const messageReactions = prev[targetMessageId] || {
                    thumbs_up: [],
                    thumbs_down: [],
                    heart: [],
                    celebrate: [],
                    wave: [],
                    laugh: [],
                    exclamation: []
                  };
                  
                  // Check if user already reacted with this type
                  const existingReaction = messageReactions[reactionType].find(
                    r => r.userId === reactionUserId
                  );
                  
                  if (existingReaction) {
                    // Remove reaction (toggle off)
                    logger.log('[IVS Chat] Removing reaction:', reactionType, 'from user:', reactionUserId);
                    return {
                      ...prev,
                      [targetMessageId]: {
                        ...messageReactions,
                        [reactionType]: messageReactions[reactionType].filter(
                          r => r.userId !== reactionUserId
                        )
                      }
                    };
                  } else {
                    // Add reaction
                    logger.log('[IVS Chat] Adding reaction:', reactionType, 'from user:', reactionUserId);
                    return {
                      ...prev,
                      [targetMessageId]: {
                        ...messageReactions,
                        [reactionType]: [
                          ...messageReactions[reactionType],
                          {
                            type: reactionType,
                            userId: reactionUserId,
                            username: reactionUsername
                          }
                        ]
                      }
                    };
                  }
                });
              } else if (isFromCurrentUser) {
                logger.log('[IVS Chat] Skipping reaction from current user (already have optimistic update)');
              }
            } catch (e) {
              logger.warn('Failed to process reaction:', e);
            }
            return; // Don't display reactions as regular messages
          }
          
          // Also check for reaction in content format [REACTION:messageId:reactionType]
          if (messageContent.startsWith('[REACTION:') && messageContent.endsWith(']')) {
            try {
              const parts = messageContent.substring(10, messageContent.length - 1).split(':');
              if (parts.length >= 2) {
                const targetMessageId = parts[0];
                const reactionType = parts[1] as ReactionType;
                logger.log('[IVS Chat] Received reaction from content format:', { targetMessageId, reactionType });
              }
            } catch (e) {
              logger.warn('Failed to parse reaction from content:', e);
            }
            return; // Don't display reactions as regular messages
          }

          // Extract message ID first (before we use it)
          const messageId = message.Id || 
                           message.id || 
                           message.messageId || 
                           message.RequestId ||
                           message.requestId || 
                           message.eventId || 
                           `${messageUserId}-${Date.now()}-${Math.random()}`;
          
          logger.log('[IVS Chat] Storing message with ID:', messageId, 'Raw message fields:', {
            Id: message.Id,
            id: message.id,
            messageId: message.messageId,
            RequestId: message.RequestId
          });

          // Check if this might be a server echo of a recently sent local message
          // Look for a local message with the same content sent within the last 5 seconds
          const now = new Date();
          let foundLocalMessage = false;
          
            setMessages(prev => {
            // Find recent local message with matching content (within 5 seconds)
            const recentLocalMessage = prev.find(msg => 
              msg.isLocal && 
              msg.message === messageContent &&
              (now.getTime() - msg.timestamp.getTime()) < 5000
            );
            
            if (recentLocalMessage) {
              foundLocalMessage = true;
              // Update the local message with server info (including the real server ID!)
              logger.log('[IVS Chat] Updating local message ID from', recentLocalMessage.id, 'to server ID', messageId);
              
              // Also migrate any reactions from the old ID to the new ID
              const oldId = recentLocalMessage.id;
              if (oldId !== messageId) {
                setReactions(prevReactions => {
                  if (prevReactions[oldId]) {
                    const { [oldId]: oldReactions, ...rest } = prevReactions;
                    return {
                      ...rest,
                      [messageId]: oldReactions
                    };
                  }
                  return prevReactions;
                });
              }
              
                return prev.map(msg => 
                msg.id === recentLocalMessage.id ? {
                    ...msg,
                  id: messageId, // ✅ Update to use server's ID for deletion
                    username: messageUsername || msg.username,
                    isIntersectStaff: isIntersectStaff,
                    chatType: chatTypeFromMsg as ChatMessageType || msg.chatType
                  } : msg
                );
              }
              return prev;
            });
            
          // If we found and updated a local message, skip adding this as a new message
          if (foundLocalMessage) {
            // Update local username if this is from us
            if (isFromCurrentUser && messageUsername && messageUsername !== 'Unknown') {
              setLocalUsername(messageUsername);
            }
            if (isFromCurrentUser && isIntersectStaff) {
              setLocalIsIntersectStaffFromAPI(true);
            }
            return;
          }

          // If this is from the current user but no local message was found, update user info
          if (isFromCurrentUser) {
            if (messageUsername && messageUsername !== 'Unknown') {
              setLocalUsername(messageUsername);
            }
            if (isIntersectStaff) {
              setLocalIsIntersectStaffFromAPI(true);
            }
            // Still skip if it's from current user (edge case where local message wasn't added yet)
            return;
          }
          
          // Check if we already have this message (avoid duplicates)
          setMessages(prev => {
            // Check by ID first
            const existsById = prev.find(msg => msg.id === messageId);
            if (existsById) {
              logger.log('Message already exists by ID, skipping:', messageId);
              return prev;
            }

            // Also check by content and timestamp to avoid duplicates from different sources
            const sendTime = message.SendTime || message.sendTime || message.timestamp;
            const messageTimestamp = sendTime ? new Date(sendTime) : new Date();
            
            // Use username from Sender.Attributes if available, otherwise use userId
            const displayUsername = message.Sender?.Attributes?.username || 
                                   message.Sender?.Attributes?.userId ||
                                   messageUserId;

            // All server messages appear on the left (as received messages)
            const newMessage: ChatMessage = {
              id: messageId,
              userId: messageUserId,
              username: displayUsername,
              message: messageContent,
              timestamp: messageTimestamp,
              isLocal: false, // Server messages always appear on left
              isIntersectStaff,
              chatType: chatTypeFromMsg as ChatMessageType
            };

            logger.log('Adding new message:', newMessage);
            
            // Store timestamp for reactions API
            const timestampMs = messageTimestamp.getTime();
            setMessageTimestamps(prevTimestamps => ({ ...prevTimestamps, [messageId]: timestampMs }));
            
            // Increment unread count if chat is closed and we haven't counted this message yet
            if (!isOpenRef.current && !countedMessageIds.current.has(messageId)) {
              countedMessageIds.current.add(messageId);
              setUnreadCount(prev => prev + 1);
            }
            
            return [...prev, newMessage];
          });
        };

        unsubscribeFunctions.push(chatRoom.addListener('message', handleMessage));
        
        // Listen for events (reactions) - LEGACY FALLBACK
        // Note: Reactions are now sent as regular messages with special attributes
        // This listener is kept as a fallback in case AWS adds native event support
        const handleEvent = (event: any) => {
          logger.log('Received chat event:', event);
          logger.log('Event keys:', Object.keys(event));
          logger.log('Full event structure:', JSON.stringify(event, null, 2));
          
          // Handle both EVENT type and direct event objects
          const eventType = event.Type || event.type;
          const eventName = event.EventName || event.eventName || event.Event;
          const attributes = event.Attributes || event.attributes || {};
          
          // Also check Sender for user info
          const senderUserId = event.Sender?.UserId || 
                              event.Sender?.Attributes?.userId ||
                              event.senderId ||
                              event.userId;
          
          logger.log('Event details:', { eventType, eventName, attributes, senderUserId });
          
          if ((eventType === 'EVENT' || !eventType) && eventName === 'message_reaction') {
            const messageId = attributes.messageId || attributes.MessageId || attributes.message_id;
            const reactionType = attributes.reactionType || attributes.reaction_type;
            const reactionUserId = attributes.userId || attributes.user_id || senderUserId;
            const reactionUsername = attributes.username || attributes.display_name || reactionUserId;
            
            logger.log('Processing reaction event:', { messageId, reactionType, reactionUserId, reactionUsername });
            
            // Skip if this is from the current user (we already have optimistic update)
            const isFromCurrentUser = reactionUserId === userId || 
                                     reactionUserId === localUsername ||
                                     senderUserId === userId ||
                                     senderUserId === localUsername;
            
            if (isFromCurrentUser) {
              logger.log('Skipping reaction event from current user (already have optimistic update)');
              return;
            }
            
            if (messageId && reactionType && (reactionType === 'thumbs_up' || reactionType === 'thumbs_down' || reactionType === 'heart' || reactionType === 'celebrate' || reactionType === 'wave' || reactionType === 'laugh' || reactionType === 'exclamation')) {
              setReactions(prev => {
                const messageReactions = prev[messageId] || {
                  thumbs_up: [],
                  thumbs_down: [],
                  heart: [],
                  celebrate: [],
                  wave: [],
                  laugh: [],
                  exclamation: []
                };
                
                // Check if user already reacted with this type
                const existingReaction = messageReactions[reactionType as ReactionType].find(
                  r => r.userId === reactionUserId
                );
                
                if (existingReaction) {
                  // Remove reaction (toggle off)
                  logger.log('Removing reaction:', reactionType, 'from user:', reactionUserId);
                  return {
                    ...prev,
                    [messageId]: {
                      ...messageReactions,
                      [reactionType]: messageReactions[reactionType as ReactionType].filter(
                        r => r.userId !== reactionUserId
                      )
                    }
                  };
                } else {
                  // Add reaction
                  logger.log('Adding reaction:', reactionType, 'from user:', reactionUserId);
                  return {
                    ...prev,
                    [messageId]: {
                      ...messageReactions,
                      [reactionType]: [
                        ...messageReactions[reactionType as ReactionType],
                        {
                          type: reactionType as ReactionType,
                          userId: reactionUserId,
                          username: reactionUsername
                        }
                      ]
                    }
                  };
                }
              });
            } else {
              logger.warn('Invalid reaction event data:', { messageId, reactionType });
            }
          } else {
            logger.log('Event is not a message_reaction event:', { eventType, eventName });
          }
        };
        
        unsubscribeFunctions.push(chatRoom.addListener('event', handleEvent));
        
        // Also try other possible event names
        if (typeof (chatRoom as any).addListener === 'function') {
          try {
            unsubscribeFunctions.push((chatRoom as any).addListener('messageReceived', handleMessage));
            unsubscribeFunctions.push((chatRoom as any).addListener('chatMessage', handleMessage));
            unsubscribeFunctions.push((chatRoom as any).addListener('eventReceived', handleEvent));
          } catch (e) {
            // Ignore if these events don't exist
          }
        }

        // Connect to the chat room
        await chatRoom.connect();

      } catch (error) {
        console.error('Error connecting to IVS chat room:', error);
        setIsConnected(false);
      }
    };

    connectToChatRoom();

    // Cleanup
    return () => {
      logger.log('[IVS Chat] Cleaning up chat connection...');
      
      // Unsubscribe from all event listeners
      unsubscribeFunctions.forEach(unsubscribe => {
        try {
          unsubscribe();
        } catch (e) {
          console.error('[IVS Chat] Error unsubscribing:', e);
        }
      });
      
      if (chatRoomRef.current) {
        try {
          logger.log('[IVS Chat] Disconnecting from chat room...');
          chatRoomRef.current.disconnect();
          logger.log('[IVS Chat] Disconnected from chat room');
        } catch (error) {
          console.error('[IVS Chat] Error disconnecting chat room:', error);
        }
        chatRoomRef.current = null;
      }
      
      // Clear typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      logger.log('[IVS Chat] Cleanup complete');
    };
  }, [roomArn, messagingEndpoint, accessToken, userId]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !isConnected || !chatRoomRef.current) return;

    const messageText = inputMessage.trim();
    setInputMessage('');

    // Clear typing state when sending message
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (isTypingRef.current) {
      isTypingRef.current = false;
      sendTypingStatusToUnity(false);
    }

    // Add message to local state immediately for better UX
    // Only staff can send non-default message types
    const effectiveChatType = localIsIntersectStaff ? selectedChatType : 'default';
    
    const localMessage: ChatMessage = {
      id: `local-${Date.now()}-${Math.random()}`,
      userId: userId || 'Unknown',
      username: localUsername,
      message: messageText,
      timestamp: new Date(),
      isLocal: true,
      isIntersectStaff: localIsIntersectStaff,
      chatType: effectiveChatType
    };
    setMessages(prev => [...prev, localMessage]);

    // Send chat message to Unity
    if (window.sendMessageToUnity) {
      try {
        const chatData = {
          eventType: 'Chat',
          payload: JSON.stringify({
            message: messageText
          })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(chatData));
        logger.log('[Chat→Unity] Sent chat message to Unity:', messageText);
      } catch (error) {
        console.error('[Chat→Unity] Error sending to Unity:', error);
      }
    }

    try {
      logger.log('Sending message:', messageText, 'with chat type:', effectiveChatType);
      // Send message via ChatRoom
      const requestId = `msg-${Date.now()}-${Math.random()}`;
      
      // Build message attributes - only include chatType if not default (to save bandwidth)
      const messageAttributes: Record<string, string> = {};
      if (effectiveChatType !== 'default') {
        messageAttributes.chatType = effectiveChatType;
      }
      
      const result = await chatRoomRef.current.sendMessage({
        id: requestId,
        content: messageText,
        action: 'SEND_MESSAGE',
        requestId: requestId,
        ...(Object.keys(messageAttributes).length > 0 && { attributes: messageAttributes })
      } as any);
      logger.log('Message send result:', result);
      
      // Persist message to backend (fire-and-forget, don't block UI)
      const messageId = result?.id || requestId;
      const timestamp = Date.now();
      
      // Store timestamp for potential reactions
      setMessageTimestamps(prev => ({ ...prev, [messageId]: timestamp }));
      
      // Save to backend
      if (accessToken && eventId && roomId) {
        API.postChatMessage(accessToken, {
          eventId,
          roomId,
          messageId,
          message: messageText,
          chatType: effectiveChatType,
          isIntersectStaff: localIsIntersectStaff
        }).catch(err => console.error('[IVS Chat] Error persisting message:', err));
      }
      
      // Note: Unity capture is re-enabled by onBlur event when input loses focus
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove the optimistic message if send failed
      setMessages(prev => prev.filter(msg => msg.id !== localMessage.id));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!localIsIntersectStaff || !roomId || !eventId || !accessToken) return;

    setIsDeletingMessage(true);
    try {
      // Call the backend API endpoint to delete the message via AWS IVS Chat API
      const response = await API.deleteIVSChatMessage(accessToken, {
        eventId: eventId,
        roomId: roomId,
        id: messageId,
        reason: 'Moderator action'
      });

      if (response.status !== 200) {
        throw new Error(`Failed to delete message: ${response.status}`);
      }

      const data = await response.json();
      logger.log('[IVS Chat] Message deleted from IVS, operation ID:', data.id);

      // Also delete from persistent storage (fire-and-forget)
      API.deleteChatMessage(accessToken, {
        eventId,
        roomId,
        messageId,
        reason: 'Moderator action'
      }).then(() => {
        logger.log('[IVS Chat] Message deleted from persistent storage:', messageId);
      }).catch(err => {
        console.error('[IVS Chat] Error deleting message from persistent storage:', err);
      });

      // Remove message locally (AWS will also send a messageDelete event to all clients)
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      setDeleteConfirmMessageId(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      // Optionally show an error message to the user
    } finally {
      setIsDeletingMessage(false);
    }
  };

  const parsePodiumPosition = (positionStr: string): { x: number; y: number; z: number } | null => {
    try {
      // Parse format like "(187.3, -0.5, 76.7)"
      const cleaned = positionStr.replace(/[()]/g, '').trim();
      const parts = cleaned.split(',').map(p => parseFloat(p.trim()));
      
      if (parts.length === 3 && parts.every(p => !isNaN(p))) {
        return { x: parts[0], y: parts[1], z: parts[2] };
      }
      return null;
    } catch (error) {
      console.error('Error parsing podium position:', error);
      return null;
    }
  };

  const handleTeleportUser = (message: ChatMessage) => {
    if (!localIsIntersectStaff || !podiumPosition) return;
    setTeleportConfirmMessage(message);
  };

  const handleConfirmTeleport = async () => {
    if (!teleportConfirmMessage || !podiumPosition || !chatRoomRef.current) return;

    const position = parsePodiumPosition(podiumPosition);
    
    if (!position) {
      console.error('[IVS Chat] Invalid podium position format');
      setTeleportConfirmMessage(null);
      return;
    }

    const targetUserId = teleportConfirmMessage.userId;
    const isTargetingSelf = targetUserId === userId || targetUserId === localUsername;

    logger.log('[IVS Chat] Sending moderator teleport command:', {
      targetUser: targetUserId,
      position,
      isTargetingSelf
    });
    
    try {
      // Send message through IVS Chat that target user will receive
      const teleportCommand = {
        type: 'MOD_TELEPORT',
        targetUserId: targetUserId,
        position: position,
        rotation: { x: 0, y: 0, z: 0 }
      };

      const requestId = `teleport-${Date.now()}-${Math.random()}`;
      await chatRoomRef.current.sendMessage({
        id: requestId,
        content: JSON.stringify(teleportCommand),
        action: 'SEND_MESSAGE',
        requestId: requestId,
        attributes: {
          messageType: 'MOD_TELEPORT',
          targetUserId: targetUserId
        }
      } as any);

      logger.log('[IVS Chat] Moderator teleport command sent successfully');
      
      // If teleporting self, immediately execute locally (don't wait for IVS message)
      if (isTargetingSelf && window.sendMessageToUnity) {
        logger.log('[IVS Chat] Teleporting self - executing immediately');
        const teleportPayload = {
          eventType: 'Teleport',
          payload: JSON.stringify({
            position: position,
            rotation: { x: 0, y: 0, z: 0 }
          })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportPayload));
        
        if (onPodiumTeleport) {
          onPodiumTeleport();
        }
      }
    } catch (error) {
      console.error('[IVS Chat] Error sending teleport command:', error);
    }
    
    setTeleportConfirmMessage(null);
  };

  const handleCancelTeleport = () => {
    setTeleportConfirmMessage(null);
  };

  const handleGrantScreenShare = (message: ChatMessage) => {
    if (!localIsIntersectStaff) return;
    setScreenShareConfirmMessage(message);
  };

  const handleConfirmGrantScreenShare = async () => {
    if (!screenShareConfirmMessage || !chatRoomRef.current) return;

    const targetUserId = screenShareConfirmMessage.userId;
    const isTargetingSelf = targetUserId === userId || targetUserId === localUsername;

    logger.log('[IVS Chat] Sending screen share permission grant:', {
      targetUser: targetUserId,
      isTargetingSelf
    });
    
    try {
      // Send message through IVS Chat that target user will receive
      const grantCommand = {
        type: 'MOD_GRANT_SCREENSHARE',
        targetUserId: targetUserId
      };

      const requestId = `screenshare-grant-${Date.now()}-${Math.random()}`;
      await chatRoomRef.current.sendMessage({
        id: requestId,
        content: JSON.stringify(grantCommand),
        action: 'SEND_MESSAGE',
        requestId: requestId,
        attributes: {
          messageType: 'MOD_GRANT_SCREENSHARE',
          targetUserId: targetUserId
        }
      } as any);

      logger.log('[IVS Chat] Screen share permission grant sent successfully');
      
      // If granting to self, immediately update local state (don't wait for IVS message)
      if (isTargetingSelf && onScreenSharePermissionGranted) {
        logger.log('[IVS Chat] Granting permission to self - updating immediately');
        onScreenSharePermissionGranted();
      }
    } catch (error) {
      console.error('[IVS Chat] Error sending screen share grant:', error);
    }
    
    setScreenShareConfirmMessage(null);
  };

  const handleCancelGrantScreenShare = () => {
    setScreenShareConfirmMessage(null);
  };

  const handleRevokeScreenShare = (message: ChatMessage) => {
    if (!localIsIntersectStaff) return;
    setScreenShareRevokeConfirmMessage(message);
  };

  const handleConfirmRevokeScreenShare = async () => {
    if (!screenShareRevokeConfirmMessage || !chatRoomRef.current) return;

    const targetUserId = screenShareRevokeConfirmMessage.userId;
    const isTargetingSelf = targetUserId === userId || targetUserId === localUsername;

    logger.log('[IVS Chat] Sending screen share permission revoke:', {
      targetUser: targetUserId,
      isTargetingSelf
    });
    
    try {
      // Send message through IVS Chat that target user will receive
      const revokeCommand = {
        type: 'MOD_REVOKE_SCREENSHARE',
        targetUserId: targetUserId
      };

      const requestId = `screenshare-revoke-${Date.now()}-${Math.random()}`;
      await chatRoomRef.current.sendMessage({
        id: requestId,
        content: JSON.stringify(revokeCommand),
        action: 'SEND_MESSAGE',
        requestId: requestId,
        attributes: {
          messageType: 'MOD_REVOKE_SCREENSHARE',
          targetUserId: targetUserId
        }
      } as any);

      logger.log('[IVS Chat] Screen share permission revoke sent successfully');
      
      // If revoking from self, immediately update local state (don't wait for IVS message)
      if (isTargetingSelf && onScreenSharePermissionRevoked) {
        logger.log('[IVS Chat] Revoking permission from self - updating immediately');
        onScreenSharePermissionRevoked();
      }
    } catch (error) {
      console.error('[IVS Chat] Error sending screen share revoke:', error);
    }
    
    setScreenShareRevokeConfirmMessage(null);
  };

  const handleCancelRevokeScreenShare = () => {
    setScreenShareRevokeConfirmMessage(null);
  };

  const handleReaction = async (messageId: string, reactionType: ReactionType) => {
    if (!isConnected || !chatRoomRef.current) return;

    const currentUserId = userId || localUsername;
    
    // Optimistically update reactions immediately
    setReactions(prev => {
      const messageReactions = prev[messageId] || {
        thumbs_up: [],
        thumbs_down: [],
        heart: [],
        celebrate: [],
        wave: [],
        laugh: [],
        exclamation: []
      };
      
      // Check if user already reacted with this type
      const existingReaction = messageReactions[reactionType].find(
        r => r.userId === currentUserId
      );
      
      if (existingReaction) {
        // Remove reaction (toggle off)
        return {
          ...prev,
          [messageId]: {
            ...messageReactions,
            [reactionType]: messageReactions[reactionType].filter(
              r => r.userId !== currentUserId
            )
          }
        };
      } else {
        // Add reaction
        return {
          ...prev,
          [messageId]: {
            ...messageReactions,
            [reactionType]: [
              ...messageReactions[reactionType],
              {
                type: reactionType,
                userId: currentUserId,
                username: localUsername
              }
            ]
          }
        };
      }
    });

    try {
      // Send reaction as a regular message with special attributes (like delete commands)
      const requestId = `reaction-${Date.now()}-${Math.random()}`;
      await chatRoomRef.current.sendMessage({
        id: requestId,
        content: `[REACTION:${messageId}:${reactionType}]`, // Special content format
        action: 'SEND_MESSAGE',
        requestId: requestId,
        attributes: {
          messageType: 'REACTION',
          targetMessageId: messageId,
          reactionType: reactionType,
          userId: currentUserId,
          username: localUsername
        }
      } as any);
      
      logger.log('[IVS Chat] Sent reaction:', { messageId, reactionType, userId: currentUserId });
      
      // Persist reaction to backend (fire-and-forget)
      const timestamp = messageTimestamps[messageId];
      if (timestamp && accessToken && eventId && roomId) {
        API.postChatReaction(accessToken, {
          eventId,
          roomId,
          messageId,
          timestamp,
          reactionType
        }).catch(err => console.error('[IVS Chat] Error persisting reaction:', err));
      }
    } catch (error) {
      console.error('Error sending reaction:', error);
      // Revert optimistic update on error
      setReactions(prev => {
        const messageReactions = prev[messageId];
        if (!messageReactions) return prev;
        
        const existingReaction = messageReactions[reactionType].find(
          r => r.userId === currentUserId
        );
        
        if (existingReaction) {
          // Remove the optimistic reaction we just added
          return {
            ...prev,
            [messageId]: {
              ...messageReactions,
              [reactionType]: messageReactions[reactionType].filter(
                r => r.userId !== currentUserId
              )
            }
          };
        } else {
          // Re-add the reaction we just removed
          return {
            ...prev,
            [messageId]: {
              ...messageReactions,
              [reactionType]: [
                ...messageReactions[reactionType],
                {
                  type: reactionType,
                  userId: currentUserId,
                  username: localUsername
                }
              ]
            }
          };
        }
      });
    }
  };

  if (!roomArn || !messagingEndpoint) {
    return null;
  }

  return (
    <>
      {/* Chat Button - Bottom Left */}
      {!isOpen && (
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent click from reaching Unity canvas
            setIsOpen(true);
            // Release cursor so it's visible when opening chat (but keep keyboard for movement)
            releaseCursorOnly();
          }}
          className="absolute bottom-4 left-4 p-3 bg-blue-600 bg-opacity-80 text-white rounded-full hover:bg-opacity-100 transition-all duration-200 z-10 flex items-center justify-center shadow-lg"
          style={{ zIndex: 9999, pointerEvents: 'auto' }}
          title="Open chat"
        >
          <FaComments size={20} />
          {/* Unread message badge */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-md animate-pulse">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat Panel - Bottom Left */}
      {isOpen && (
        <div
          ref={chatContainerRef}
          className="absolute bottom-4 left-4 w-[360px] max-w-[90vw] h-[320px] max-h-[55vh] flex flex-col bg-[#1a1a1a] border border-[#333] rounded-lg shadow-2xl z-10"
          style={{ zIndex: 9999, pointerEvents: 'auto' }}
          data-tour-id={dataTourId}
          onClick={(e) => {
            // Stop propagation to prevent clicks from going to Unity canvas,
            // but don't disable Unity capture here - only do that in the input field onFocus
            e.stopPropagation();
          }}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-3 border-b border-[#333] bg-[#2a2a2a] rounded-t-lg">
            <div className="flex items-center gap-2">
              <h3 className="text-white text-base font-semibold m-0">
                {roomName || 'Chat'}
              </h3>
              <span 
                className={`w-2 h-2 rounded-full ${
                  isConnected 
                    ? 'bg-green-400 shadow-[0_0_4px_rgba(74,222,128,0.5)]' 
                    : 'bg-yellow-500'
                }`}
              ></span>
            </div>
            <button
              onClick={() => {
                setIsOpen(false);
                // Don't manipulate Unity capture here - input field onBlur already handles it
              }}
              className="p-1 text-[#999] hover:text-white transition-colors"
              title="Close chat"
            >
              <FaTimes size={16} />
            </button>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-[#1a1a1a] [&::-webkit-scrollbar-thumb]:bg-[#444] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-[#555]">
            {messages.length === 0 ? (
              <div className="text-center text-[#666] text-sm py-8">
                No messages yet. Start the conversation!
              </div>
            ) : (
              messages.map((msg) => {
                const messageReactions = reactions[msg.id] || {
                  thumbs_up: [],
                  thumbs_down: [],
                  heart: [],
                  celebrate: [],
                  wave: [],
                  laugh: [],
                  exclamation: []
                };
                const hasReactions = messageReactions.thumbs_up.length > 0 || 
                                   messageReactions.thumbs_down.length > 0 || 
                                   messageReactions.heart.length > 0 ||
                                   messageReactions.celebrate.length > 0 ||
                                   messageReactions.wave.length > 0 ||
                                   messageReactions.laugh.length > 0 ||
                                   messageReactions.exclamation.length > 0;
                const isHovered = hoveredMessageId === msg.id;
                
                return (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${msg.isLocal ? 'items-end' : 'items-start'}`}
                    onMouseEnter={() => setHoveredMessageId(msg.id)}
                    onMouseLeave={() => setHoveredMessageId(null)}
                  >
                    <div className="relative group">
                      <div
                        className={`min-w-[220px] max-w-[95%] rounded-lg px-3 py-2 ${
                          msg.chatType === 'announcement'
                            ? 'bg-yellow-900/40 border border-yellow-500/50'
                            : msg.chatType === 'system'
                            ? 'bg-red-900/40 border border-red-500/50'
                            : msg.isLocal
                            ? 'bg-blue-600'
                            : 'bg-[#2a2a2a] border border-[#444]'
                        }`}
                      >
                        {/* Header row with username/label on left and timestamp on right */}
                        <div className={`text-xs mb-1 font-medium flex items-center gap-1.5 min-w-0 w-full ${
                          msg.chatType === 'announcement'
                            ? 'text-yellow-300'
                            : msg.chatType === 'system'
                            ? 'text-red-300'
                            : msg.isIntersectStaff 
                            ? 'text-yellow-400' 
                            : (msg.isLocal ? 'text-blue-100' : 'text-[#999]')
                        }`}>
                          {msg.chatType === 'announcement' ? (
                            <>
                              <FaBullhorn className="w-3 h-3 flex-shrink-0 text-yellow-400" />
                              <span className="text-yellow-300 font-semibold">ANNOUNCEMENT</span>
                            </>
                          ) : msg.chatType === 'system' ? (
                            <>
                              <FaExclamationTriangle className="w-3 h-3 flex-shrink-0 text-red-400" />
                              <span className="text-red-300 font-semibold">SYSTEM</span>
                            </>
                          ) : (
                            <>
                              {msg.isIntersectStaff && (
                                <img 
                                  src={intersectStaffIcon} 
                                  alt="Intersect Staff" 
                                  className="w-3 h-3 flex-shrink-0"
                                />
                              )}
                              <span 
                                className="break-words overflow-wrap-anywhere"
                                style={{
                                  fontSize: msg.username.length > 20 ? '0.65rem' : '0.75rem',
                                  lineHeight: '1.2',
                                  wordBreak: 'break-word',
                                  maxWidth: '100%'
                                }}
                              >
                                {msg.username}
                              </span>
                            </>
                          )}
                          {/* Timestamp on the right */}
                          <span className={`ml-auto flex-shrink-0 font-normal ${
                            msg.chatType === 'announcement'
                              ? 'text-yellow-500/70'
                              : msg.chatType === 'system'
                              ? 'text-red-500/70'
                              : msg.isLocal 
                              ? 'text-blue-200/70' 
                              : 'text-[#666]'
                          }`}>
                            {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className={`text-sm break-words ${
                          msg.chatType === 'announcement'
                            ? 'text-yellow-400'
                            : msg.chatType === 'system'
                            ? 'text-red-400'
                            : 'text-white'
                        }`}>{msg.message}</div>
                      </div>
                      
                      {/* Persistent reactions - positioned at bottom of message, slightly overlapping */}
                      {hasReactions && (
                        <div className={`absolute ${msg.isLocal ? 'right-2' : 'left-2'} bottom-[-8px] flex items-center gap-1 z-10 pointer-events-auto`}>
                          {messageReactions.thumbs_up.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'thumbs_up')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Thumbs up"
                            >
                              <FaThumbsUp className="text-blue-400" size={10} />
                              <span className="text-[#999]">{messageReactions.thumbs_up.length}</span>
                            </button>
                          )}
                          {messageReactions.thumbs_down.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'thumbs_down')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Thumbs down"
                            >
                              <FaThumbsDown className="text-red-400" size={10} />
                              <span className="text-[#999]">{messageReactions.thumbs_down.length}</span>
                            </button>
                          )}
                          {messageReactions.heart.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'heart')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Heart"
                            >
                              <FaHeart className="text-pink-400" size={10} />
                              <span className="text-[#999]">{messageReactions.heart.length}</span>
                            </button>
                          )}
                          {messageReactions.celebrate.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'celebrate')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Celebrate"
                            >
                              <FaRocket className="text-yellow-400" size={10} />
                              <span className="text-[#999]">{messageReactions.celebrate.length}</span>
                            </button>
                          )}
                          {messageReactions.wave.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'wave')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Wave"
                            >
                              <FaHandPaper className="text-green-400" size={10} />
                              <span className="text-[#999]">{messageReactions.wave.length}</span>
                            </button>
                          )}
                          {messageReactions.laugh.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'laugh')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Laugh"
                            >
                              <FaLaughSquint className="text-amber-400" size={10} />
                              <span className="text-[#999]">{messageReactions.laugh.length}</span>
                            </button>
                          )}
                          {messageReactions.exclamation.length > 0 && (
                            <button
                              onClick={() => handleReaction(msg.id, 'exclamation')}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors hover:bg-[#2a2a2a] bg-[#1a1a1a] border border-[#333]"
                              title="Exclamation"
                            >
                              <FaExclamation className="text-orange-400" size={10} />
                              <span className="text-[#999]">{messageReactions.exclamation.length}</span>
                            </button>
                          )}
                        </div>
                      )}
                      
                      {/* Reaction buttons and mod actions - show on hover above message */}
                      {isHovered && (
                        <div 
                          className={`absolute ${msg.isLocal ? 'left-2' : 'right-2'} -top-4 flex flex-col gap-1 bg-[#1a1a1a] border border-[#333] rounded-lg p-1 shadow-lg z-10 pointer-events-auto`}
                          style={{ transform: localIsIntersectStaff ? 'translateY(-24px)' : 'translateY(0)' }}
                        >
                          {/* Reactions row */}
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'thumbs_up');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.thumbs_up.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Thumbs up"
                            >
                              <FaThumbsUp className="text-blue-400" size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'thumbs_down');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.thumbs_down.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Thumbs down"
                            >
                              <FaThumbsDown className="text-red-400" size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'heart');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.heart.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Heart"
                            >
                              <FaHeart className="text-pink-400" size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'celebrate');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.celebrate.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Celebrate"
                            >
                              <FaRocket className="text-yellow-400" size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'wave');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.wave.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Wave"
                            >
                              <FaHandPaper className="text-green-400" size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'laugh');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.laugh.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Laugh"
                            >
                              <FaLaughSquint className="text-amber-400" size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReaction(msg.id, 'exclamation');
                              }}
                              className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
                                messageReactions.exclamation.some(r => r.userId === userId || r.userId === localUsername) ? 'bg-[#2a2a2a]' : ''
                              }`}
                              title="Exclamation"
                            >
                              <FaExclamation className="text-orange-400" size={14} />
                            </button>
                          </div>
                          
                          {/* Mod actions row - separate row below reactions */}
                          {localIsIntersectStaff && (
                            <div className="flex items-center gap-1 pt-1 border-t border-[#333]">
                              <span className="text-[10px] text-[#666] px-1">MOD:</span>
                              {podiumPosition && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    logger.log('[IVS Chat] Teleport button clicked for user:', msg.userId);
                                    handleTeleportUser(msg);
                                  }}
                                  className="p-1.5 rounded hover:bg-blue-900/50 transition-colors"
                                  title={msg.isLocal ? "Teleport yourself to podium" : "Teleport user to podium"}
                                >
                                  <FaMapMarkerAlt className="text-blue-400" size={14} />
                                </button>
                              )}
                              {/* Grant Screen Share Permission Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  logger.log('[IVS Chat] Grant screen share clicked for user:', msg.userId);
                                  handleGrantScreenShare(msg);
                                }}
                                className="p-1.5 rounded hover:bg-green-900/50 transition-colors"
                                title={msg.isLocal ? "Grant yourself screen share permission" : "Grant screen share permission"}
                              >
                                <FaDesktop className="text-green-400" size={14} />
                              </button>
                              {/* Revoke Screen Share Permission Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  logger.log('[IVS Chat] Revoke screen share clicked for user:', msg.userId);
                                  handleRevokeScreenShare(msg);
                                }}
                                className="p-1.5 rounded hover:bg-red-900/50 transition-colors"
                                title={msg.isLocal ? "Revoke your screen share permission" : "Revoke screen share permission"}
                              >
                                <FaDesktop className="text-red-400" size={14} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirmMessageId(msg.id);
                                }}
                                className="p-1.5 rounded hover:bg-red-900/50 transition-colors"
                                title="Delete message"
                              >
                                <FaTrash className="text-red-400" size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-3 border-t border-[#333] bg-[#2a2a2a] rounded-b-lg">
            <div className="flex gap-2 items-center">
              {/* Chat Type Selector - Only for Staff/Moderators */}
              {localIsIntersectStaff && (
                <div className="relative">
                  <button
                    onClick={() => setShowChatTypeDropdown(!showChatTypeDropdown)}
                    className={`p-2 rounded-md transition-colors flex items-center gap-1 ${
                      selectedChatType === 'announcement' 
                        ? 'bg-yellow-600 text-white hover:bg-yellow-700' 
                        : selectedChatType === 'system'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-[#1a1a1a] border border-[#444] text-[#999] hover:text-white hover:border-[#555]'
                    }`}
                    title={`Chat type: ${selectedChatType === 'default' ? 'Default' : selectedChatType === 'announcement' ? 'Announcement' : 'System'}`}
                  >
                    {selectedChatType === 'announcement' ? (
                      <FaBullhorn size={14} />
                    ) : selectedChatType === 'system' ? (
                      <FaExclamationTriangle size={14} />
                    ) : (
                      <FaComments size={14} />
                    )}
                    <FaChevronDown size={10} className={`transition-transform ${showChatTypeDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showChatTypeDropdown && (
                    <div className="absolute bottom-full left-0 mb-2 bg-[#1a1a1a] border border-[#444] rounded-lg shadow-xl overflow-hidden min-w-[160px] z-20">
                      <button
                        onClick={() => {
                          setSelectedChatType('default');
                          setShowChatTypeDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#2a2a2a] transition-colors ${
                          selectedChatType === 'default' ? 'bg-[#2a2a2a] text-white' : 'text-[#999]'
                        }`}
                      >
                        <FaComments size={12} className="text-[#999]" />
                        <span>Default</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedChatType('announcement');
                          setShowChatTypeDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#2a2a2a] transition-colors ${
                          selectedChatType === 'announcement' ? 'bg-yellow-900/30 text-yellow-400' : 'text-[#999]'
                        }`}
                      >
                        <FaBullhorn size={12} className="text-yellow-400" />
                        <span className="text-yellow-400">Announcement</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedChatType('system');
                          setShowChatTypeDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#2a2a2a] transition-colors ${
                          selectedChatType === 'system' ? 'bg-red-900/30 text-red-400' : 'text-[#999]'
                        }`}
                      >
                        <FaExclamationTriangle size={12} className="text-red-400" />
                        <span className="text-red-400">System</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                onFocus={() => {
                  setIsInputFocused(true);
                }}
                onBlur={() => {
                  setIsInputFocused(false);
                }}
                placeholder={
                  !isConnected 
                    ? "Connecting..." 
                    : selectedChatType === 'announcement' && localIsIntersectStaff
                    ? "Type an announcement..."
                    : selectedChatType === 'system' && localIsIntersectStaff
                    ? "Type a system message..."
                    : "Type a message..."
                }
                disabled={!isConnected}
                className={`flex-1 px-3 py-2 bg-[#1a1a1a] border rounded-md text-sm placeholder-[#666] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                  selectedChatType === 'announcement' && localIsIntersectStaff
                    ? 'border-yellow-500/50 text-yellow-400 focus:border-yellow-500'
                    : selectedChatType === 'system' && localIsIntersectStaff
                    ? 'border-red-500/50 text-red-400 focus:border-red-500'
                    : 'border-[#444] text-white focus:border-blue-500'
                }`}
              />
              <button
                onClick={handleSendMessage}
                disabled={!isConnected || !inputMessage.trim()}
                className={`px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                  selectedChatType === 'announcement' && localIsIntersectStaff
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : selectedChatType === 'system' && localIsIntersectStaff
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
                title="Send message"
              >
                <FaPaperPlane size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmMessageId && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10002]"
          style={{ pointerEvents: 'auto' }}
          onClick={() => !isDeletingMessage && setDeleteConfirmMessageId(null)}
        >
          <div 
            className="bg-[#1a1a1a] rounded-lg shadow-2xl border border-[#333] p-6 max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <img 
                src={intersectStaffIcon} 
                alt="Mod Action" 
                className="w-5 h-5"
              />
              Moderator Action: Delete Message
            </h3>
            <p className="text-gray-300 mb-6">
              As a moderator, you are about to delete this message. This action cannot be undone and will remove the message for all users in the room.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmMessageId(null)}
                disabled={isDeletingMessage}
                className="px-4 py-2 bg-[#2a2a2a] text-white rounded-md hover:bg-[#333] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteMessage(deleteConfirmMessageId)}
                disabled={isDeletingMessage}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeletingMessage ? (
                  <>
                    <FaSpinner size={14} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash size={14} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teleport User Confirmation Modal */}
      {teleportConfirmMessage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10002]"
          style={{ pointerEvents: 'auto' }}
          onClick={handleCancelTeleport}
        >
          <div 
            className="bg-[#1a1a1a] rounded-lg shadow-2xl border border-blue-600 p-6 max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <img 
                src={intersectStaffIcon} 
                alt="Mod Action" 
                className="w-5 h-5"
              />
              Moderator Action: Teleport User
            </h3>
            <div className="mb-6 space-y-3">
              <p className="text-gray-300">
                Send teleport command to <span className="text-blue-400 font-semibold">{teleportConfirmMessage.username}</span>?
              </p>
              <p className="text-gray-400 text-sm">
                They will be teleported to the podium on their screen.
              </p>
              <div className="bg-[#2a2a2a] rounded-lg p-3">
                <div className="text-xs text-[#999] mb-1">Podium Position:</div>
                <div className="text-sm text-white font-mono">
                  {podiumPosition}
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelTeleport}
                className="px-4 py-2 bg-[#2a2a2a] text-white rounded-md hover:bg-[#333] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmTeleport}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <FaMapMarkerAlt size={14} />
                Teleport
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grant Screen Share Permission Confirmation Modal */}
      {screenShareConfirmMessage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10002]"
          style={{ pointerEvents: 'auto' }}
          onClick={handleCancelGrantScreenShare}
        >
          <div 
            className="bg-[#1a1a1a] rounded-lg shadow-2xl border border-green-600 p-6 max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <img 
                src={intersectStaffIcon} 
                alt="Mod Action" 
                className="w-5 h-5"
              />
              Moderator Action: Grant Screen Share
            </h3>
            <div className="mb-6 space-y-3">
              <p className="text-gray-300">
                Grant screen share permission to <span className="text-green-400 font-semibold">{screenShareConfirmMessage.username}</span>?
              </p>
              <p className="text-gray-400 text-sm">
                They will be able to share their screen to the stage.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelGrantScreenShare}
                className="px-4 py-2 bg-[#2a2a2a] text-white rounded-md hover:bg-[#333] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmGrantScreenShare}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <FaDesktop size={14} />
                Grant Permission
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Screen Share Permission Confirmation Modal */}
      {screenShareRevokeConfirmMessage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10002]"
          style={{ pointerEvents: 'auto' }}
          onClick={handleCancelRevokeScreenShare}
        >
          <div 
            className="bg-[#1a1a1a] rounded-lg shadow-2xl border border-red-600 p-6 max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <img 
                src={intersectStaffIcon} 
                alt="Mod Action" 
                className="w-5 h-5"
              />
              Moderator Action: Revoke Screen Share
            </h3>
            <div className="mb-6 space-y-3">
              <p className="text-gray-300">
                Revoke screen share permission from <span className="text-red-400 font-semibold">{screenShareRevokeConfirmMessage.username}</span>?
              </p>
              <p className="text-gray-400 text-sm">
                They will no longer be able to share their screen.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelRevokeScreenShare}
                className="px-4 py-2 bg-[#2a2a2a] text-white rounded-md hover:bg-[#333] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRevokeScreenShare}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <FaDesktop size={14} />
                Revoke Permission
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

IVSRealtimeChat.displayName = 'IVSRealtimeChat';

export default IVSRealtimeChat;
